import { SLOVAK_MUNICIPALITIES } from "./slovakMunicipalities.ts";
import type { CitySelection, SlovakMunicipality } from "./types.ts";

export const normalizeLocationText = (value: string) =>
  value
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("sk-SK")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const normalizeIdPart = (value: string) =>
  normalizeLocationText(value)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const municipalityIdFor = (name: string, district = "") =>
  `municipality:${normalizeIdPart(name)}:${normalizeIdPart(district) || "unknown"}`;

const indexedMunicipalities = SLOVAK_MUNICIPALITIES.map((municipality) => ({
  municipality,
  name: normalizeLocationText(municipality.name),
  district: normalizeLocationText(municipality.district)
}));

export const findMunicipality = (value: string): SlovakMunicipality | undefined => {
  const normalized = normalizeLocationText(value);
  if (!normalized) return undefined;
  return indexedMunicipalities.find((entry) => entry.name === normalized)?.municipality;
};

export const searchMunicipalities = (query: string, limit = 7): SlovakMunicipality[] => {
  const normalized = normalizeLocationText(query);
  if (!normalized) return [];

  return indexedMunicipalities
    .flatMap((entry) => {
      const words = entry.name.split(" ");
      const rank = entry.name === normalized
        ? 0
        : entry.name.startsWith(normalized)
          ? 1
          : words.some((word) => word.startsWith(normalized))
            ? 2
            : entry.name.includes(normalized)
              ? 3
              : entry.district.includes(normalized)
                ? 4
                : undefined;
      return rank === undefined ? [] : [{ ...entry, rank }];
    })
    .sort((first, second) => first.rank - second.rank || first.municipality.name.localeCompare(second.municipality.name, "sk"))
    .slice(0, Math.max(1, limit))
    .map((entry) => entry.municipality);
};

export const datasetCitySelection = (municipality: SlovakMunicipality): CitySelection => ({
  city: municipality.name,
  cityNormalized: normalizeLocationText(municipality.name),
  citySource: "dataset",
  municipalityId: municipality.id ?? municipality.lau2Code
});

export const customCitySelection = (city: string): CitySelection => ({
  city,
  cityNormalized: normalizeLocationText(city),
  citySource: "custom",
  municipalityId: undefined
});
